import type { FieldNode } from 'graphql';

/**
 * ТЗ 0128 — постраничная колонка доски (лиды и договоры) в кэше Apollo.
 *
 * Колонка спрашивает поле списка под псевдонимом `boardColumn`
 * (`boardColumn: kommoLeads(…)`, `boardColumn: contractDocuments(…)`). Только
 * для него ключ записи — фильтры БЕЗ `offset`/`limit`: страницы `fetchMore`
 * дописываются к одной записи по `offset`, и колонка видит их все — «Показать
 * ещё» добирает ровно новую страницу, а не перечитывает колонку с нуля с
 * растущим `limit` (и без потолка 100/200).
 *
 * Тот же список без псевдонима (таблица со страницами, счётчики) хранится как
 * раньше — по всем аргументам, каждая страница отдельно.
 *
 * Слияние:
 *  - `offset > 0` — страница ложится на своё место (дальше — обрезается, если
 *    сервер вернул меньше `limit`: список кончился);
 *  - `offset = 0` — свежая голова колонки. Если она не короче загруженного или
 *    список на ней кончился, колонка заменяется целиком (перечитывание после
 *    переноса просит `limit` = сколько загружено); иначе (фоновое
 *    перечитывание первой страницы) заменяется только голова, хвост остаётся —
 *    колонка не схлопывается до 30 карточек. Хвост — старые карточки ПОСЛЕ
 *    последней карточки свежей головы (а не после 30-й по счёту): новая
 *    карточка сверху сдвигает голову, и старая 30-я не пропадает («дыры» до
 *    следующей страницы нет); ушедшая из головы карточка не возвращается;
 *  - сдвиг (лид ушёл из колонки) может задвоить карточку на стыке —
 *    `read` убирает повторы по ссылке.
 */
export const BOARD_COLUMN_ALIAS = 'boardColumn';

type IArgs = Record<string, unknown> | null;

const PAGING_ARGS = ['offset', 'limit'];

export const isBoardColumnField = (field: FieldNode | null | undefined) =>
  field?.alias?.value === BOARD_COLUMN_ALIAS;

/** Аргументы без страницы — в стабильном порядке ключей. */
const filterKey = (args: IArgs): string => {
  const out: Record<string, unknown> = {};
  Object.keys(args || {})
    .filter((key) => !PAGING_ARGS.includes(key))
    .sort()
    .forEach((key) => {
      out[key] = (args as Record<string, unknown>)[key];
    });
  return JSON.stringify(out);
};

/**
 * Ключ записи: колонка доски — `board:<фильтры без страницы>`; остальное —
 * все аргументы (как по умолчанию).
 */
export const boardKeyArgs = (
  args: IArgs,
  context: { field: FieldNode | null },
): string | string[] | undefined => {
  if (isBoardColumnField(context.field)) return `board:${filterKey(args)}`;
  return args ? Object.keys(args) : undefined;
};

/** Ключ карточки: ссылка кэша, `id` или сам элемент. */
const itemKey = <T>(item: T): unknown => {
  const record = (item as unknown) as Record<string, unknown> | null;
  if (record && typeof record === 'object') {
    return record.__ref ?? record.id ?? item;
  }
  return item;
};

/**
 * Хвост колонки под свежей головой: старые карточки после последней
 * карточки головы, что была и в старом списке; таких нет — после стольких
 * карточек, сколько в голове. Карточки головы из хвоста убраны.
 */
const tailAfterHead = <T>(
  existing: ReadonlyArray<T>,
  incoming: ReadonlyArray<T>,
): T[] => {
  const headKeys = new Set(incoming.map(itemKey));
  const positions = new Map<unknown, number>();
  existing.forEach((item, index) => {
    const key = itemKey(item);
    if (!positions.has(key)) positions.set(key, index);
  });
  let start = incoming.length;
  for (let i = incoming.length - 1; i >= 0; i -= 1) {
    const at = positions.get(itemKey(incoming[i]));
    if (at !== undefined) {
      start = at + 1;
      break;
    }
  }
  return existing.slice(start).filter((item) => !headKeys.has(itemKey(item)));
};

/** Слить страницу списка по `offset` (см. описание модуля). */
export const mergeBoardItems = <T>(
  existing: ReadonlyArray<T> | undefined,
  incoming: ReadonlyArray<T>,
  args: IArgs,
): T[] => {
  const offset = Math.max(0, Number(args?.offset) || 0);
  const limit = Number(args?.limit) || 0;
  const ended = limit > 0 && incoming.length < limit;
  if (!existing || offset === 0) {
    if (!existing || ended || incoming.length >= existing.length) {
      return incoming.slice();
    }
    return [...incoming, ...tailAfterHead(existing, incoming)];
  }
  const start = Math.min(offset, existing.length);
  const head = existing.slice(0, start);
  const tail = ended ? [] : existing.slice(start + incoming.length);
  return [...head, ...incoming, ...tail];
};

/** Без повторов (по ссылке кэша или id); нет повторов — тот же массив. */
export const dedupeBoardItems = <T>(
  items: ReadonlyArray<T>,
): ReadonlyArray<T> => {
  const keyOf = itemKey;
  const seen = new Set<unknown>();
  let dup = false;
  items.forEach((item) => {
    const key = keyOf(item);
    if (seen.has(key)) dup = true;
    seen.add(key);
  });
  if (!dup) return items;
  const kept = new Set<unknown>();
  return items.filter((item) => {
    const key = keyOf(item);
    if (kept.has(key)) return false;
    kept.add(key);
    return true;
  });
};

interface IFieldOptions {
  args: IArgs;
  field: FieldNode | null;
}

/** Политика поля-СПИСКА (`contractDocuments`). */
export const boardListPolicy = {
  keyArgs: boardKeyArgs,
  merge(
    existing: ReadonlyArray<unknown> | undefined,
    incoming: ReadonlyArray<unknown>,
    { args, field }: IFieldOptions,
  ) {
    if (!isBoardColumnField(field)) return incoming;
    return mergeBoardItems(existing, incoming, args);
  },
  read(existing: ReadonlyArray<unknown> | undefined, { field }: IFieldOptions) {
    if (!existing || !isBoardColumnField(field)) return existing;
    return dedupeBoardItems(existing);
  },
};

interface IPage {
  items?: ReadonlyArray<unknown>;
  [key: string]: unknown;
}

/** Политика поля-СТРАНИЦЫ `{ items, total }` (`kommoLeads`). */
export const boardPagePolicy = {
  keyArgs: boardKeyArgs,
  merge(
    existing: IPage | undefined,
    incoming: IPage,
    { args, field }: IFieldOptions,
  ) {
    if (!isBoardColumnField(field) || !incoming || !incoming.items) {
      return incoming;
    }
    return {
      ...existing,
      ...incoming,
      items: mergeBoardItems(existing?.items, incoming.items, args),
    };
  },
  read(existing: IPage | undefined, { field }: IFieldOptions) {
    if (!existing || !existing.items || !isBoardColumnField(field)) {
      return existing;
    }
    const items = dedupeBoardItems(existing.items);
    return items === existing.items ? existing : { ...existing, items };
  },
};
